import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Container, Card, Badge, Button, Spinner, Alert, Table } from 'react-bootstrap';
import { ApiGet, ApiPut } from '../../../../ApiServices/ApiServices';
import { toast } from 'react-toastify';

const KitchenDisplay = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('pending');
  const [orders, setOrders] = useState([]);
  const [refreshing, setRefreshing] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const autoRefreshInterval = useRef(null);

  // Status mapping for API
  const statusMapping = {
    pending: 'PENDING',
    accepted: 'ACCEPTED_ORDER',
    preparing: 'PREPARING_ORDER',
    ready: 'READY_FOR_ORDER'
  };

  // Fetch orders for current active tab status
  const fetchOrders = useCallback(async (status, showLoader = false, search = '') => {
    if (showLoader) setLoading(true);
    setRefreshing(true);
    setError('');

    try {
      const response = await ApiGet('/api/kitchen/orders/filter', {
        searchValue: search,
        status: statusMapping[status] || ''
      });

      if (response.success) {
        const records = response.success.data?.data?.records || response.success.data?.records || [];
        setOrders(Array.isArray(records) ? records : []);
      } else {
        console.error(`Failed to fetch orders:`, response.fail);
        setOrders([]);
      }
    } catch (err) {
      setError('Failed to fetch orders. Please try again.');
      console.error('Error fetching orders:', err);
      setOrders([]);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  // Initial load and auto-refresh setup
  useEffect(() => {
    // Only call API if searchValue is empty or has more than 3 characters
    if (searchValue.length === 0 || searchValue.length > 3) {
      fetchOrders(activeTab, true, searchValue);
    }

    // Auto-refresh every 15 seconds
    autoRefreshInterval.current = setInterval(() => {
      if (searchValue.length === 0 || searchValue.length > 3) {
        fetchOrders(activeTab, false, searchValue);
      }
    }, 15000);

    return () => {
      if (autoRefreshInterval.current) {
        clearInterval(autoRefreshInterval.current);
      }
    };
  }, [activeTab, fetchOrders, searchValue]);

  // Handle tab change
  const handleTabChange = (tabId) => {
    setActiveTab(tabId);
    fetchOrders(tabId, true, searchValue);
  };

  const handleRefresh = () => {
    fetchOrders(activeTab, false, searchValue);
  };

  // Handle search
  const handleSearch = (e) => {
    const value = e.target.value;
    setSearchValue(value);
  };

  const handleAcceptOrder = async (order) => {
    try {
      const response = await ApiPut('/api/kitchen/orders/update', {
        id: order.id,
        status: 'ACCEPTED_ORDER'
      });

      if (response.success) {
        toast.success(`Order ${order.orderNumber} accepted`);
        fetchOrders(activeTab, false, searchValue);
      } else {
        toast.error(response.fail || 'Failed to update order');
      }
    } catch (err) {
      toast.error('Error updating order');
    }
  };

  const handleStartPreparing = async (order) => {
    try {
      const response = await ApiPut('/api/kitchen/orders/update', {
        id: order.id,
        status: 'PREPARING_ORDER'
      });

      if (response.success) {
        toast.success(`Order ${order.orderNumber} moved to Preparing`);
        fetchOrders(activeTab, false, searchValue);
      } else {
        toast.error(response.fail || 'Failed to update order');
      }
    } catch (err) {
      toast.error('Error updating order');
    }
  };

  const handleMarkReady = async (order) => {
    try {
      const response = await ApiPut('/api/kitchen/orders/update', {
        id: order.id,
        status: 'READY_FOR_ORDER'
      });

      if (response.success) {
        toast.success(`Order ${order.orderNumber} marked as Ready`);
        fetchOrders(activeTab, false, searchValue);
      } else {
        toast.error(response.fail || 'Failed to update order');
      }
    } catch (err) {
      toast.error('Error updating order');
    }
  };

  const formatTime = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleTimeString('en-IN', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getTimeDiff = (dateString) => {
    if (!dateString) return '';
    const diff = Math.floor((new Date() - new Date(dateString)) / 60000);
    if (diff < 1) return 'Just now';
    if (diff < 60) return `${diff}m ago`;
    return `${Math.floor(diff / 60)}h ${diff % 60}m ago`;
  };

  const TabButton = ({ id, label, isActive }) => (
    <button
      onClick={() => handleTabChange(id)}
      style={{
        padding: '10px 16px',
        border: 'none',
        borderRadius: '8px',
        fontWeight: 'bold',
        fontSize: '13px',
        cursor: 'pointer',
        backgroundColor: isActive ? '#3b82f6' : '#f1f5f9',
        color: isActive ? 'white' : '#64748b',
        display: 'flex',
        alignItems: 'center',
        gap: '8px'
      }}
    >
      {label}
      {isActive && (
        <span style={{
          backgroundColor: 'rgba(255,255,255,0.2)',
          color: 'white',
          padding: '2px 8px',
          borderRadius: '12px',
          fontSize: '12px'
        }}>
          {orders.length}
        </span>
      )}
    </button>
  );

  return (
    <Container fluid className="py-3">
      {/* Header */}
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h3 className="mb-0 fw-bold" style={{ color: '#1e293b' }}>
          <i className="bi bi-display me-2"></i>
          Kitchen Display
        </h3>
        <div className="d-flex align-items-center gap-3">
          <small className="text-muted">Auto-refresh: 15s</small>
          <Button
            variant="primary"
            onClick={handleRefresh}
            disabled={refreshing}
            style={{ backgroundColor: '#3b82f6', borderColor: '#3b82f6' }}
          >
            {refreshing ? (
              <Spinner animation="border" size="sm" style={{ width: '16px', height: '16px', borderWidth: '2px' }} />
            ) : (
              <i className="bi bi-arrow-clockwise"></i>
            )}
          </Button>
        </div>
      </div>

      {/* Error Alert */}
      {error && (
        <Alert variant="danger" dismissible onClose={() => setError('')} className="mb-4">
          {error}
        </Alert>
      )}

      {/* Tab Buttons */}
      <div className="mb-4 d-flex flex-wrap gap-2 align-items-center">
        <TabButton id="pending" label="New Orders" isActive={activeTab === 'pending'} />
        <TabButton id="accepted" label="ACCEPTED_ORDER" isActive={activeTab === 'accepted'} />
        <TabButton id="preparing" label="PREPARING_ORDER" isActive={activeTab === 'preparing'} />
        <TabButton id="ready" label="ReadyOrder" isActive={activeTab === 'ready'} />
        <input
          type="text"
          placeholder="Search orders..."
          value={searchValue}
          onChange={handleSearch}
          style={{
            padding: '10px 14px',
            borderRadius: '8px',
            border: '1px solid #e2e8f0',
            fontSize: '13px',
            outline: 'none',
            width: '200px',
            marginLeft: '10px'
          }}
        />
      </div>

      {/* Orders Table */}
      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" variant="primary" />
          <div className="mt-2 text-muted">Loading orders...</div>
        </div>
      ) : orders.length === 0 ? (
        <Card className="border-0 shadow-sm">
          <Card.Body className="text-center py-5">
            <i className="bi bi-inbox fs-1 text-muted d-block mb-3" style={{ opacity: 0.5 }}></i>
            <h5 className="text-muted">
              {activeTab === 'pending' && 'No New Orders'}
              {activeTab === 'accepted' && 'No Accepted Orders'}
              {activeTab === 'preparing' && 'No Preparing Orders'}
              {activeTab === 'ready' && 'No Ready Orders'}
            </h5>
          </Card.Body>
        </Card>
      ) : (
        <Card className="border-0 shadow-sm">
          <Card.Body className="p-0">
            <Table responsive hover className="mb-0">
              <thead>
                <tr style={{ backgroundColor: '#3b82f6' }}>
                  {activeTab !== 'ready' && <th style={{ backgroundColor: '#3b82f6', color: 'white', border: 'none', padding: '12px 16px', textAlign: 'center' }}>Action</th>}
                  <th style={{ backgroundColor: '#3b82f6', color: 'white', border: 'none', padding: '12px 16px', textAlign: 'center' }}>Order Id</th>
                  <th style={{ backgroundColor: '#3b82f6', color: 'white', border: 'none', padding: '12px 16px', textAlign: 'center' }}>Time</th>
                  <th style={{ backgroundColor: '#3b82f6', color: 'white', border: 'none', padding: '12px 16px', textAlign: 'center' }}>Ordered Items</th>
                  <th style={{ backgroundColor: '#3b82f6', color: 'white', border: 'none', padding: '12px 16px', textAlign: 'center' }}>Customer Info</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((order, index) => (
                  <tr key={order.id} style={{ backgroundColor: index % 2 === 0 ? '#ffffff' : '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                    {activeTab !== 'ready' && (
                      <td className="py-3 align-middle" style={{ padding: '16px', textAlign: 'center' }}>
                        <Button
                          variant="outline-success"
                          size="sm"
                          onClick={() => {
                            if (activeTab === 'pending') handleAcceptOrder(order);
                            else if (activeTab === 'accepted') handleStartPreparing(order);
                            else if (activeTab === 'preparing') handleMarkReady(order);
                          }}
                          style={{ fontWeight: '500', padding: '6px 16px' }}
                        >
                          {activeTab === 'pending' && 'Accept'}
                          {activeTab === 'accepted' && 'Start'}
                          {activeTab === 'preparing' && 'Ready'}
                        </Button>
                      </td>
                    )}
                    <td className="py-3 align-middle" style={{ padding: '16px', textAlign: 'center' }}>
                      <div className="fw-bold" style={{ fontSize: '15px' }}>{order.orderNumber}</div>
                      <div className="mt-2 d-flex gap-1 justify-content-center">
                        <Badge bg={order.orderType === 'DINE_IN' ? 'primary' : order.orderType === 'ONLINE' ? 'success' : 'info'}>
                          {order.orderType?.replace('_', ' ') || 'N/A'}
                        </Badge>
                        {order.tableNumber && (
                          <Badge bg="dark">{order.tableNumber}</Badge>
                        )}
                      </div>
                    </td>
                    <td className="py-3 align-middle" style={{ padding: '16px', textAlign: 'center' }}>
                      <div className="text-muted" style={{ fontSize: '14px' }}>{formatTime(order.createdAt)}</div>
                      <Badge bg="danger" className="mt-1">{getTimeDiff(order.createdAt)}</Badge>
                    </td>
                    <td className="py-3 align-middle" style={{ padding: '16px', textAlign: 'center' }}>
                      <details style={{ cursor: 'pointer' }}>
                        <summary style={{ fontSize: '14px', fontWeight: '500', listStyle: 'none' }}>
                          {(order.orderItems || []).map((item, idx) => (
                            <div key={idx} style={{ marginBottom: '2px' }}>
                              {item.quantity}x {item.menuItemName || item.name || 'Item'}
                            </div>
                          ))}
                          <span style={{
                            fontSize: '12px',
                            color: '#3b82f6',
                            display: 'inline-flex',
                            alignItems: 'center',
                            marginTop: '4px',
                            textDecoration: 'underline'
                          }}>
                            <i className="bi bi-eye me-1"></i>
                            View Details
                          </span>
                        </summary>
                        <div style={{ marginTop: '8px', paddingLeft: '10px', borderLeft: '2px solid #e2e8f0' }}>
                          {(order.orderItems || []).map((item, idx) => (
                            <div key={idx} style={{ fontSize: '13px', marginBottom: '8px', paddingBottom: '8px', borderBottom: idx < order.orderItems.length - 1 ? '1px dashed #e2e8f0' : 'none' }}>
                              <div className="fw-bold">{item.menuItemName || item.name || 'Item'}</div>
                              <div style={{ fontSize: '12px', color: '#6b7280' }}>Qty: {item.quantity}</div>
                              {item.specialInstructions && (
                                <div style={{ fontSize: '12px', color: '#f59e0b', marginTop: '4px' }}>
                                  <i className="bi bi-info-circle me-1"></i>
                                  {item.specialInstructions}
                                </div>
                              )}
                              {item.addonItems && item.addonItems.length > 0 && (
                                <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                                  <strong>Addons:</strong>
                                  {item.addonItems.map((addon, addonIdx) => (
                                    <div key={addonIdx} style={{ paddingLeft: '8px' }}>
                                      + {addon.name} (x{addon.quantity})
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      </details>
                    </td>
                    <td className="py-3 align-middle" style={{ padding: '16px', textAlign: 'center' }}>
                      <div style={{ fontSize: '15px', fontWeight: '500' }}>{order.customerName || 'N/A'}</div>
                      {order.customerPhone && (
                        <div style={{ fontSize: '13px', color: '#6b7280', marginTop: '2px' }}>
                          <i className="bi bi-telephone me-1"></i>
                          {order.customerPhone}
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </Card.Body>
        </Card>
      )}
    </Container>
  );
};

export default KitchenDisplay;
